// src/common/report-generator.js

/**
 * Generate HTML report from verified footnotes and pages.
 * 
 * @param {Object} options - Configuration options
 * @param {Array} options.pages - Array of page objects
 * @param {Array} options.footnotes - Array of footnote objects
 * @param {Object} options.resultsCache - ResultsCache instance or Map with audit results
 * @param {string} options.documentId - Current document ID
 * @param {string} options.documentPath - Document path
 * @param {string} options.apiBase - API base URL
 * @param {Function} options.getAuthHeaders - Function to get auth headers
 * @param {string} options.pdfName - Name of the PDF file (default: "document.pdf")
 * @returns {Promise<Blob>} HTML file as blob
 */
export async function generateHtmlReport(options = {}) {
  const {
    pages,
    footnotes,
    resultsCache,
    documentId,
    documentPath,
    apiBase,
    getAuthHeaders,
    pdfName = "document.pdf",
  } = options;

  if (!pages || pages.length === 0) {
    throw new Error("No pages available. Please parse PDF first.");
  }

  if (!footnotes || footnotes.length === 0) {
    throw new Error("No citations available. Please parse PDF footnotes or inline citations first.");
  }

  const isNonEmptyObject = (value) => value && typeof value === 'object' && Object.keys(value).length > 0;
  const resultLookupKeys = (fn) => {
    const keys = [];
    const push = (value) => {
      if (value === undefined || value === null || value === '') return;
      const key = String(value);
      if (!keys.includes(key)) keys.push(key);
      if (key.startsWith('fn-')) {
        const unprefixed = key.slice(3);
        if (unprefixed && !keys.includes(unprefixed)) keys.push(unprefixed);
      } else if (!keys.includes(`fn-${key}`)) {
        keys.push(`fn-${key}`);
      }
    };
    push(fn.id);
    push(fn.reference_id);
    push(fn.footnote_id);
    return keys;
  };

  const getCachedResultsForReportItem = (fn) => {
    const candidates = resultLookupKeys(fn);
    let latestResults = null;
    for (const key of candidates) {
      if (resultsCache && typeof resultsCache.getResults === 'function') {
        latestResults = resultsCache.getResults(key);
      } else if (resultsCache && resultsCache.get) {
        latestResults = resultsCache.get(key);
      }
      if (isNonEmptyObject(latestResults)) break;
    }

    // Merge rather than replace: cached_results may contain citation metadata
    // while resultsCache has the freshest verification verdicts.
    return {
      ...(isNonEmptyObject(fn.cached_results) ? fn.cached_results : {}),
      ...(isNonEmptyObject(latestResults) ? latestResults : {}),
    };
  };

  // Prepare data for the endpoint
  // Merge latest verification results from resultsCache into footnotes
  const requestData = {
    pages: pages.map(page => ({
      page_number: page.page_number || 0,
      markdown: page.markdown || page.main_text || "",
      main_text: page.main_text || page.markdown || ""
    })),
    footnotes: footnotes.map(fn => {
      const cached_results = getCachedResultsForReportItem(fn);
      
      return {
        id: fn.id,
        reference_id: fn.reference_id,
        text: fn.text || "",
        context: fn.context || "",
        full_context: fn.full_context || "",
        page_context: fn.page_context || "",
        page_numbers: fn.page_numbers || [],
        heading_id: fn.heading_id || null,
        cached_results: cached_results,
        cached_citations: fn.cached_citations || [],
        is_inline: fn.is_inline || false,
        parsed_citations: fn.parsed_citations || null,
        inline_citation: fn.inline_citation || null
      };
    }),
    pdf_name: pdfName,
    document_id: documentId,
    document_path: documentPath
  };

  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${apiBase}/pdf/generate_html_report`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders
    },
    body: JSON.stringify(requestData)
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to generate HTML report: ${errorText}`);
  }

  // Return the blob for download
  return await response.blob();
}

/**
 * Generate an annotated PDF report: a copy of the user's *original* PDF with
 * native highlight annotations + popup comments on each cited passage.
 *
 * Unlike generateHtmlReport (which rebuilds the document from OCR markdown),
 * this sends the original PDF bytes so the backend can anchor real PDF
 * annotations to the actual pages. Output opens in any reader / Acrobat's
 * comment pane.
 *
 * @param {Object} options - Configuration options
 * @param {Uint8Array|ArrayBuffer} options.fileBytes - Original PDF bytes
 * @param {Array} options.footnotes - Array of footnote objects
 * @param {Object} options.resultsCache - ResultsCache instance or Map with audit results
 * @param {string} options.apiBase - API base URL
 * @param {Function} options.getAuthHeaders - Function to get auth headers
 * @param {string} options.pdfName - Name of the PDF file (default: "document.pdf")
 * @returns {Promise<Blob>} Annotated PDF file as blob
 */
export async function generatePdfReport(options = {}) {
  const {
    fileBytes,
    footnotes,
    resultsCache,
    apiBase,
    getAuthHeaders,
    pdfName = "document.pdf",
  } = options;

  if (!fileBytes || !fileBytes.length) {
    throw new Error("Original PDF bytes are not available for annotation.");
  }

  if (!footnotes || footnotes.length === 0) {
    throw new Error("No citations available. Please parse PDF footnotes or inline citations first.");
  }

  // Merge latest verification results into footnotes (same logic as the HTML report).
  const footnotePayload = footnotes.map(fn => {
    const footnoteId = String(fn.id);
    let latestResults = null;
    if (resultsCache && typeof resultsCache.getResults === 'function') {
      latestResults = resultsCache.getResults(footnoteId);
    } else if (resultsCache && resultsCache.get) {
      latestResults = resultsCache.get(footnoteId);
    }
    const cached_results = latestResults || fn.cached_results || {};

    return {
      id: fn.id,
      reference_id: fn.reference_id,
      text: fn.text || "",
      page_numbers: fn.page_numbers || [],
      cached_results: cached_results,
      cached_citations: fn.cached_citations || [],
      is_inline: fn.is_inline || false,
      parsed_citations: fn.parsed_citations || null,
      inline_citation: fn.inline_citation || null,
    };
  });

  const form = new FormData();
  form.append("file", new File([fileBytes], pdfName, { type: "application/pdf" }));
  form.append("footnotes", JSON.stringify(footnotePayload));
  form.append("pdf_name", pdfName);

  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${apiBase}/pdf/generate_pdf_report`, {
    method: 'POST',
    headers: {
      // NOTE: do not set Content-Type; the browser sets the multipart boundary.
      ...authHeaders
    },
    body: form
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to generate PDF report: ${errorText}`);
  }

  return await response.blob();
}

